import { useCallback, useSyncExternalStore } from "react";
import {
  FAVORITES_KEY,
  FAVORITES_EVENT,
  normalizeFavorites,
} from "@/lib/favorites";
import { supabase } from "@/integrations/supabase/client";

/**
 * Favorites live in ONE module-level store, not in per-component React state.
 *
 * Every heart button, rail and the /saved page subscribe to the same snapshot,
 * so a save anywhere repaints everywhere in the same tick. The previous
 * implementation kept a separate useState per hook instance and only
 * re-broadcast for signed-out guests, which is why signed-in users saw /saved
 * go stale until a reload.
 *
 * Sync layers, cheapest first:
 *   1. in-memory store  → instant, same tab
 *   2. localStorage + FAVORITES_EVENT → other components/tabs for guests
 *   3. supabase realtime on user_favorites → other tabs and devices when authed
 */

type Listener = () => void;

const listeners = new Set<Listener>();

// Snapshot must be referentially stable between emits for useSyncExternalStore.
let snapshot: string[] = [];
const EMPTY: string[] = [];
let userId: string | null = null;
let started = false;
let realtimeChannel: ReturnType<typeof supabase.channel> | null = null;

/**
 * Clicks the user already made but whose DB write hasn't been confirmed yet.
 * Any snapshot coming from the server (initial hydrate, error refetch) is
 * reconciled against these so a slow fetch can never undo a fresh click.
 */
const pending = new Map<string, boolean>();

function reconcile(ids: string[]): string[] {
  if (pending.size === 0) return ids;
  const set = new Set(ids);
  for (const [id, saved] of pending) {
    if (saved) set.add(id);
    else set.delete(id);
  }
  return Array.from(set);
}

function emit() {
  for (const l of listeners) l();
}

function setSnapshot(next: string[]) {
  const a = snapshot;
  if (a.length === next.length && a.every((v, i) => v === next[i])) return;
  snapshot = next;
  emit();
}

/** Apply a server-derived list, keeping unconfirmed local intents on top. */
function setRemoteSnapshot(ids: string[]) {
  setSnapshot(reconcile(ids));
}


function readLocal(): string[] {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    return normalizeFavorites(raw ? JSON.parse(raw) : []);
  } catch {
    return [];
  }
}

function writeLocal(ids: string[]) {
  try {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(ids));
  } catch {}
  try {
    window.dispatchEvent(new Event(FAVORITES_EVENT));
  } catch {}
}

async function fetchRemote(uid: string): Promise<string[]> {
  const { data, error } = await supabase
    .from("user_favorites")
    .select("place_id")
    .eq("user_id", uid);
  if (error || !data) return [];
  return normalizeFavorites(data.map((r) => r.place_id as string));
}

/** Live push from Postgres so a save on another tab/device lands here too. */
function subscribeRealtime(uid: string) {
  void teardownRealtime();
  realtimeChannel = supabase
    .channel(`user_favorites:${uid}`)
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "user_favorites",
        filter: `user_id=eq.${uid}`,
      },
      (payload) => {
        const row = (payload.new ?? payload.old) as { place_id?: string } | null;
        const id = row?.place_id;
        if (!id) return;
        const canonical = normalizeFavorites([id])[0];
        if (!canonical) return;
        if (payload.eventType === "DELETE") {
          setSnapshot(snapshot.filter((x) => x !== canonical));
        } else if (!snapshot.includes(canonical)) {
          setSnapshot([...snapshot, canonical]);
        }
      },
    )
    .subscribe();
}

async function teardownRealtime() {
  if (!realtimeChannel) return;
  const ch = realtimeChannel;
  realtimeChannel = null;
  try {
    await supabase.removeChannel(ch);
  } catch {}
}

async function hydrate(uid: string | null) {
  userId = uid;
  if (!uid) {
    void teardownRealtime();
    setSnapshot(readLocal());
    return;
  }
  // On login, fold any guest favorites into the account, then read back.
  const local = readLocal();
  if (local.length) {
    const rows = local.map((place_id) => ({ user_id: uid, place_id }));
    const { error } = await supabase
      .from("user_favorites")
      .upsert(rows, { onConflict: "user_id,place_id" });
    if (error) {
      console.error("[favorites] failed to merge local favorites", error);
    } else {
      try { localStorage.removeItem(FAVORITES_KEY); } catch {}
    }
  }
  const remote = await fetchRemote(uid);
  if (userId !== uid) return; // auth changed while we were fetching
  setRemoteSnapshot(remote);
  subscribeRealtime(uid);
}

function start() {
  if (started || typeof window === "undefined") return;
  started = true;

  // Paint guest favorites immediately, then reconcile once auth resolves.
  setSnapshot(readLocal());

  void supabase.auth.getSession().then(({ data }) => {
    void hydrate(data.session?.user?.id ?? null);
  });

  supabase.auth.onAuthStateChange((_e, session) => {
    const next = session?.user?.id ?? null;
    window.setTimeout(() => {
      if (next === userId) return;
      void hydrate(next);
    }, 0);
  });

  window.addEventListener("storage", (e: StorageEvent) => {
    if (e.key === FAVORITES_KEY && !userId) setSnapshot(readLocal());
  });
  window.addEventListener(FAVORITES_EVENT, () => {
    if (!userId) setSnapshot(readLocal());
  });
}

function subscribe(listener: Listener) {
  start();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getSnapshot = () => snapshot;
const getServerSnapshot = () => EMPTY;

/** Toggle a favorite: optimistic locally, then persisted for signed-in users. */
async function toggleFavorite(id: string) {
  const canonical = normalizeFavorites([id])[0] ?? id;
  const shouldBeSaved = !snapshot.includes(canonical);
  pending.set(canonical, shouldBeSaved);
  const next = shouldBeSaved
    ? [...snapshot, canonical]
    : snapshot.filter((x) => x !== canonical);
  setSnapshot(next);

  // Resolve auth lazily so a click before getSession() resolves still persists.
  let uid = userId;
  if (uid === null) {
    try {
      const { data } = await supabase.auth.getSession();
      uid = data.session?.user?.id ?? null;
      userId = uid;
    } catch {
      uid = null;
    }
  }

  if (!uid) {
    pending.delete(canonical);
    writeLocal(next);
    return;
  }

  // Write the INTENT (not a read-then-write of DB truth) so rapid double
  // clicks can't land in the opposite state to what the user sees.
  const { error } = shouldBeSaved
    ? await supabase
        .from("user_favorites")
        .upsert({ user_id: uid, place_id: canonical }, { onConflict: "user_id,place_id" })
    : await supabase
        .from("user_favorites")
        .delete()
        .eq("user_id", uid)
        .eq("place_id", canonical);

  pending.delete(canonical);

  if (error) {
    console.error("[favorites] failed to persist favorite toggle", { id: canonical, error });
    setRemoteSnapshot(await fetchRemote(uid));
  }
}

export function useFavorites() {
  const favs = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const toggle = useCallback((id: string) => { void toggleFavorite(id); }, []);
  const has = useCallback((id: string) => favs.includes(id), [favs]);
  return { favs, toggle, has };
}
